/**
 * Floating Alfie chat widget (ClickUp 86d4aqt29).
 *
 * Replaces the right-edge SearchSidePanel. Owns open/closed state, which view
 * is showing, which thread is selected, and the per-thread unsent drafts.
 *
 * The selected thread id persists to localStorage so reopening the widget
 * lands back in the conversation the user left — the OPEN state itself does
 * not persist, so a page load never pops the panel open on its own.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { openClaimant } from '@/lib/alfie-context-claim';
import {
  briefSentForThread,
  type FinanceTopic,
  financeScreenActive,
  financeTopics,
  subscribeFinanceScreen,
} from '@/lib/finance-topics';
import { useFinanceTopicPick } from '@/hooks/use-finance-topic-pick';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { isUuid } from '@/api/alfie';

import { AlfieLauncher, useLauncherBottom } from './AlfieLauncher';
import { AlfieWidgetPanel } from './AlfieWidgetPanel';
import { WidgetChatView, type WidgetFinanceBar } from './WidgetChatView';
import { WidgetHistoryView } from './WidgetHistoryView';
import { useWidgetChat } from './use-widget-chat';
import { trackWidget, type WidgetOpenTrigger } from './widget-analytics';

const THREAD_STORAGE_KEY = 'alfie-widget:thread';
const TITLE_STORAGE_KEY = 'alfie-widget:thread-title';

/** Public event any component can dispatch to pop the widget open. */
export const OPEN_WIDGET_EVENT = 'openAlfieWidget';
/** Legacy name from SearchSidePanel. Its `tab` detail is ignored. */
const LEGACY_OPEN_EVENT = 'openSidePanel';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — the widget still works, just forgets */
  }
}

export function AlfieWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // The router's location, read for the analytics rows only: the avoid
  // contract measures the page, it no longer keys off the route.
  const location = useLocation();
  const isMobile = useIsMobile();
  // One measurement for both surfaces: the panel sits a fixed 16px above the
  // launcher, so when a page control lifts the disc the panel lifts with it.
  const launcherBottom = useLauncherBottom(isMobile);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [threadId, setThreadId] = useState<string | null>(() => {
    const stored = readStored(THREAD_STORAGE_KEY);
    return isUuid(stored) ? stored : null;
  });
  const [threadTitle, setThreadTitle] = useState<string | null>(() =>
    readStored(TITLE_STORAGE_KEY),
  );
  const [draft, setDraft] = useState('');
  /**
   * "Answer ready": a reply finished while the widget was shut. Cleared the
   * moment the user opens it, and on any thread change — a dot pointing at a
   * transcript that is no longer on screen is a lie.
   */
  const [newReply, setNewReply] = useState(false);

  // Unsent drafts are per-thread, keyed the same way as pages/Alfie.tsx:
  // switching conversations stashes the current draft and restores the
  // target's, so text never follows the user across chats. `null` = new chat.
  const draftsRef = useRef<Map<string | null, string>>(new Map());
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  /** Set below, once finance mode's state exists; called on every thread switch. */
  const resetFinanceTopicRef = useRef<() => void>(() => {});

  const persistThread = useCallback((id: string | null, title: string | null) => {
    setThreadId(id);
    setThreadTitle(title);
    writeStored(THREAD_STORAGE_KEY, id);
    writeStored(TITLE_STORAGE_KEY, title);
  }, []);

  /** Stash the current draft, swap threads, restore the target's draft. */
  const switchThread = useCallback(
    (toId: string | null, title: string | null) => {
      draftsRef.current.set(threadIdRef.current, draftRef.current);
      setDraft(draftsRef.current.get(toId) ?? '');
      // Covers every thread change there is: New chat, a history pick, and the
      // missing-thread rescue below all land here.
      setNewReply(false);
      resetFinanceTopicRef.current();
      persistThread(toId, title);
    },
    [persistThread],
  );

  const onThreadBound = useCallback(
    (boundId: string) => {
      // Move the new-chat draft slot onto the id Hermes just created so the
      // draft map does not strand it under `null`.
      const pending = draftsRef.current.get(null);
      if (pending !== undefined) {
        draftsRef.current.delete(null);
        draftsRef.current.set(boundId, pending);
      }
      persistThread(boundId, threadTitle);
    },
    [persistThread, threadTitle],
  );

  const chat = useWidgetChat({ threadId, onThreadBound });
  const { reset: resetChat } = chat;

  /*
    FINANCE MODE (Tim, 31 Oct 2026). While a Xero screen is mounted it registers its topics, and
    every question sent from here goes in finance mode: Hermes attaches the figures, and the
    question carries the brief of the ONE topic it is about — the same brief that topic's ✦ button
    sends. Same conversation as anywhere else; leave the screen and the next question is ordinary.
  */
  const financeOn = useSyncExternalStore(subscribeFinanceScreen, financeScreenActive, () => false);
  /** What the next question is about. Null = the figures only. Per conversation. */
  const [topicId, setTopicId] = useState<string | null>(null);
  /** The builder chose the topic from the list: the next send uses it without a pick. */
  const chosenRef = useRef(false);
  /** A question held back because two topics fit it equally well. */
  const [pending, setPending] = useState<{
    text: string;
    fileIds?: string[];
    topics: FinanceTopic[];
    ids: [string, string];
  } | null>(null);
  const { resolve: resolveTopic } = useFinanceTopicPick({
    draft,
    enabled: financeOn && isOpen,
    currentTopic: topicId,
    topics: financeTopics,
  });

  // A different conversation (New chat, a history pick) starts with no topic and no held question.
  // Not keyed on `threadId`: a new chat BINDING its first id is the same conversation.
  const resetFinanceTopic = useCallback(() => {
    setTopicId(null);
    setPending(null);
    chosenRef.current = false;
  }, []);
  resetFinanceTopicRef.current = resetFinanceTopic;

  /** Send with this topic: its brief goes only when the conversation has not had it yet. */
  const sendOnTopic = useCallback(
    (text: string, fileIds: string[] | undefined, id: string | null, topics: FinanceTopic[]) => {
      const topic = id ? topics.find((t) => t.id === id) ?? null : null;
      setTopicId(topic?.id ?? null);
      const brief = topic && briefSentForThread(threadIdRef.current) !== topic.id ? topic.brief() : null;
      return chat.send(text, fileIds, { brief, topicId: topic?.id ?? null });
    },
    [chat],
  );

  const send = useCallback(
    async (text: string, fileIds?: string[]) => {
      // Snapshot at the click: a page change while the pick is in flight does not switch mode.
      if (!financeScreenActive()) return chat.send(text, fileIds);
      const topics = financeTopics();
      if (chosenRef.current) {
        chosenRef.current = false;
        return sendOnTopic(text, fileIds, topicId, topics);
      }
      const pick = await resolveTopic(text, topics, topicId);
      if (pick.kind === 'ask') {
        setPending({ text, fileIds, topics, ids: pick.ids });
        return;
      }
      const id = pick.kind === 'topic' ? pick.id : pick.kind === 'same' ? topicId : null;
      return sendOnTopic(text, fileIds, id, topics);
    },
    [chat, resolveTopic, sendOnTopic, topicId],
  );

  const financeBar: WidgetFinanceBar | null = financeOn
    ? {
      topicLabel: topicId ? financeTopics().find((t) => t.id === topicId)?.label ?? null : null,
      listTopics: () => financeTopics().map((t) => ({ id: t.id, label: t.label })),
      onChooseTopic: (id) => {
        setTopicId(id);
        chosenRef.current = true;
      },
      pending: pending
        ? {
          text: pending.text,
          options: pending.ids.map((id) => ({
            id,
            label: pending.topics.find((t) => t.id === id)?.label ?? id,
          })),
        }
        : null,
      onPendingPick: (id) => {
        if (!pending) return;
        setPending(null);
        void sendOnTopic(pending.text, pending.fileIds, id, pending.topics);
      },
    }
    : null;

  /**
   * Raise the "answer ready" dot on the isTyping true -> false edge, but only
   * when that edge was a real reply landing. `reset()` also clears isTyping —
   * on an unbound session that is the ONLY signal it emits — so the transcript
   * is what settles it: a finished assistant turn with text in it, or nothing
   * happened worth flagging.
   */
  const wasTypingRef = useRef(chat.isTyping);
  useEffect(() => {
    const wasTyping = wasTypingRef.current;
    wasTypingRef.current = chat.isTyping;
    if (!wasTyping || chat.isTyping || isOpen) return;
    const last = chat.messages[chat.messages.length - 1];
    if (last?.role === 'assistant' && last.content.trim()) setNewReply(true);
  }, [chat.isTyping, chat.messages, isOpen]);

  /**
   * Autofocus the composer — desktop only. On a phone the software keyboard
   * slides up over the empty state's starter rows the moment it lands, so the
   * sheet opens keyboard-free and waits for the user to tap the field.
   */
  const focusComposer = useCallback(() => {
    if (isMobile) return;
    requestAnimationFrame(() => composerRef.current?.focus());
  }, [isMobile]);

  // The stored thread can disappear between sessions — archived from the full
  // /alfie page, deleted on another device. Its messages then come back empty
  // (or error), which would pin the widget to a pane it can never fill, so drop
  // the binding and start a new chat instead.
  useEffect(() => {
    if (!chat.threadMissing) return;
    resetChat();
    switchThread(null, null);
  }, [chat.threadMissing, resetChat, switchThread]);

  const newChat = useCallback(() => {
    // Reset explicitly: when the current chat never bound a thread (every send
    // failed, or nothing was sent yet) `threadId` is already null, so
    // `switchThread` alone leaves the old transcript on screen.
    resetChat();
    switchThread(null, null);
    setView('chat');
    focusComposer();
  }, [resetChat, switchThread, focusComposer]);

  // The session the analytics rows describe: when it opened and how many messages the member
  // had sent by then, so `closed` can carry seconds open and sends in the session.
  const openedAtRef = useRef<number | null>(null);
  const sentAtOpenRef = useRef(0);
  const userMessagesRef = useRef(0);
  userMessagesRef.current = chat.messages.filter((m) => m.role === 'user').length;

  const close = useCallback(() => {
    setIsOpen(false);
    if (openedAtRef.current !== null) {
      trackWidget({
        action: 'closed',
        route: location.pathname,
        threadId: threadIdRef.current,
        secondsOpen: Math.round((Date.now() - openedAtRef.current) / 1000),
        messagesSent: Math.max(0, userMessagesRef.current - sentAtOpenRef.current),
      });
      openedAtRef.current = null;
    }
    // Return focus to the control that opened the panel.
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('[data-alfie-launcher]')?.focus();
    });
  }, [location.pathname]);

  const open = useCallback((trigger: WidgetOpenTrigger = 'launcher') => {
    setIsOpen(true);
    setView('chat');
    setNewReply(false);
    focusComposer();
    openedAtRef.current = Date.now();
    sentAtOpenRef.current = userMessagesRef.current;
    trackWidget({ action: 'opened', trigger, route: location.pathname, threadId: threadIdRef.current });
  }, [focusComposer, location.pathname]);

  const toggle = useCallback((trigger: WidgetOpenTrigger = 'launcher') => {
    if (isOpen) close();
    // A SURFACE ON SCREEN MAY KNOW BETTER. While the Xero mapping flow is up it claims the
    // launcher, because the row the builder is standing on is context this widget has no way to
    // receive — its only seam discards payloads (U-2). Pressing the disc there reopens THAT
    // conversation instead of starting a general one beside it. No claim, no change: every other
    // screen in the app falls straight through.
    else if (!openClaimant()) open(trigger);
  }, [isOpen, close, open]);

  // Any component can pop the widget open. The legacy `openSidePanel` name is
  // still honoured so existing dispatchers keep working.
  useEffect(() => {
    const handler = () => open('event');
    window.addEventListener(OPEN_WIDGET_EVENT, handler);
    window.addEventListener(LEGACY_OPEN_EVENT, handler);
    return () => {
      window.removeEventListener(OPEN_WIDGET_EVENT, handler);
      window.removeEventListener(LEGACY_OPEN_EVENT, handler);
    };
  }, [open]);

  // Cmd/Ctrl+J toggles. Ignored while the user is typing in a PAGE field — the
  // old Ctrl+K binding had no such guard and stole the key from every text
  // field. The widget's own composer and search box are exempt: opening the
  // widget focuses the composer, so guarding those too would make the shortcut
  // one-way (open, never close).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'j') return;
      const el = document.activeElement as HTMLElement | null;
      const isField =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (isField && !el.closest('[data-alfie-panel]')) return;
      e.preventDefault();
      toggle('shortcut');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const expand = useCallback(() => {
    trackWidget({ action: 'opened_full_page', route: location.pathname, threadId });
    void navigate({
      to: '/alfie',
      search: threadId ? { threadId } : {},
    });
    close();
  }, [navigate, threadId, close, location.pathname]);

  const firstName = String(user?.user_metadata?.full_name ?? '').split(' ')[0] ?? '';
  // Hermes names a thread server-side, so a session that just bound its own id
  // has no title yet. Fall back to the opening question, which is what Hermes
  // titles the thread with anyway — the header should never read "New chat"
  // once the conversation has started. With neither, the chat is new by
  // definition, bound thread or not.
  const subtitle =
    threadTitle || chat.messages.find((m) => m.role === 'user')?.content || 'New chat';

  return (
    <>
      <AlfieLauncher
        isOpen={isOpen}
        onToggle={() => toggle('launcher')}
        bottomPx={launcherBottom}
        isAnswering={chat.isTyping}
        hasNewReply={newReply}
        positionKey={location.pathname}
      />
      {isOpen && (
        <AlfieWidgetPanel
          launcherBottomPx={launcherBottom}
          finance={financeOn}
          subtitle={subtitle}
          showHeader={view === 'chat'}
          onClose={close}
          onOpenHistory={() => {
            setView('history');
            trackWidget({ action: 'history_opened', threadId });
          }}
          onNewChat={newChat}
          onExpand={expand}
        >
          {view === 'chat' ? (
            <WidgetChatView
              messages={chat.messages}
              isTyping={chat.isTyping}
              isLoadingHistory={chat.isLoadingHistory}
              completedReplies={chat.completedReplies}
              firstName={firstName}
              draft={draft}
              onDraftChange={setDraft}
              onSend={send}
              finance={financeBar}
              onStop={chat.stop}
              composerRef={composerRef}
            />
          ) : (
            <WidgetHistoryView
              activeThreadId={threadId}
              onSelect={(thread) => {
                switchThread(thread.id, thread.title ?? null);
                setView('chat');
                focusComposer();
              }}
              onNewChat={newChat}
              onBack={() => setView('chat')}
              onNavigateAway={close}
              onActiveThreadDeleted={() => {
                resetChat();
                switchThread(null, null);
              }}
            />
          )}
        </AlfieWidgetPanel>
      )}
    </>
  );
}

export default AlfieWidget;
